import * as React from "react";
import { useRequestCategories } from "@/hooks/useRequestCategories";
import { useRequests } from "@/hooks/useRequests";
import { Request, RequestCategory } from "@/types/research.types";

type Mode = "list" | "create-category" | "create-request" | "edit-category" | "edit-request";

interface OpenGalleryOptions {
  type?: "discuss" | "research";
  initialData?: { name: string; content: string };
  onSelect?: (content: string, request?: Request) => void;
}

interface RequestGalleryContextType {
  searchQuery: string;
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  selectedCategoryId: string | null;
  setSelectedCategoryId: React.Dispatch<React.SetStateAction<string | null>>;
  currentParentId: string | null;
  setCurrentParentId: React.Dispatch<React.SetStateAction<string | null>>;
  categories: RequestCategory[] | undefined;
  isLoadingCategories: boolean;
  mode: Mode;
  setMode: React.Dispatch<React.SetStateAction<Mode>>;
  editingCategory: RequestCategory | null;
  editingRequest: Request | null;
  setEditingRequest: React.Dispatch<React.SetStateAction<Request | null>>;
  handleEditCategory: (category: RequestCategory) => void;
  filteredCategories: RequestCategory[];
  filteredRequests: Request[];
  selectedCategory: RequestCategory | null;
  currentCategory: RequestCategory | null;
  handleCategoryClick: (categoryId: string) => void;
  handleBackToCategories: () => void;
  handleBackToParentCategory: () => void;
  handleBack: () => void;
  activeCategoryId: string | null;
  requests: Request[] | undefined;
  isLoadingRequests: boolean;
  type: "discuss" | "research";
  activeGalleryId: string | null;
  openGallery: (id: string, options?: OpenGalleryOptions) => void;
  closeGallery: () => void;
  initialData: { name: string; content: string } | null;
  onSelect?: (content: string, request?: Request) => void;
  setType: React.Dispatch<React.SetStateAction<"discuss" | "research">>;
}

export const RequestGalleryContext = React.createContext<RequestGalleryContextType | undefined>(undefined);

export const RequestGalleryProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<string | null>(null);
  const [currentParentId, setCurrentParentId] = React.useState<string | null>(null);

  // Global state
  const [activeGalleryId, setActiveGalleryId] = React.useState<string | null>(null);
  const [type, setType] = React.useState<"discuss" | "research">("discuss");
  const [initialData, setInitialData] = React.useState<{ name: string; content: string } | null>(null);
  const [onSelect, setOnSelect] = React.useState<((content: string, request?: Request) => void) | undefined>(undefined);

  const { data: allCategories, isLoading: isLoadingCategories } = useRequestCategories();

  const categories = React.useMemo(() => {
    if (!allCategories) return [];
    return allCategories.filter((cat) => cat.chat_type === type);
  }, [allCategories, type]);

  const { data: requests, isLoading: isLoadingRequests } = useRequests(type);

  const [mode, setMode] = React.useState<Mode>("list");
  const [editingCategory, setEditingCategory] = React.useState<RequestCategory | null>(null);
  const [editingRequest, setEditingRequest] = React.useState<Request | null>(null);

  React.useEffect(() => {
    if (searchQuery) {
      setCurrentParentId(null);
    }
  }, [searchQuery]);

  const filteredCategories = React.useMemo(() => {
    if (!categories) return [];

    const categoryMap = new Map(categories.map((c) => [c._id, c]));

    const isReachable = (category: RequestCategory, visited = new Set<string>()): boolean => {
      if (!category.parent_id || category.parent_id === "default") return true;
      if (visited.has(category._id)) return false;
      visited.add(category._id);

      const parent = categoryMap.get(category.parent_id);
      if (!parent) return false;

      return isReachable(parent, visited);
    };

    const reachableCategories = categories.filter((c) => isReachable(c));

    if (!searchQuery.trim()) return reachableCategories;

    return reachableCategories.filter(
      (cat) =>
        cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (cat.description ?? "").toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [categories, searchQuery]);

  const filteredRequests = React.useMemo(() => {
    if (!requests || !categories) return [];

    // If no search query, we don't need global filtered requests (RequestList handles category-specific filtering)
    if (!searchQuery.trim()) return [];

    const categoryMap = new Map(categories.map((c) => [c._id, c]));

    const isReachable = (categoryId: string | null, visited = new Set<string>()): boolean => {
      if (!categoryId || categoryId === "default") return true; // Root requests or default category
      if (visited.has(categoryId)) return false;
      visited.add(categoryId);

      const parent = categoryMap.get(categoryId);
      // If parent category doesn't exist in our filtered list (e.g. wrong type), it's not reachable
      if (!parent) return false;

      if (!parent.parent_id || parent.parent_id === "default") return true;
      return isReachable(parent.parent_id, visited);
    };

    return requests.filter((req) => {
      // 1. Match search query
      const matchesSearch =
        req.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        req.content.toLowerCase().includes(searchQuery.toLowerCase());

      if (!matchesSearch) return false;

      // 2. Check reachability of its category
      return isReachable(req.category_id);
    });
  }, [requests, categories, searchQuery]);

  const selectedCategory = React.useMemo(() => {
    if (!selectedCategoryId || !categories) return null;
    return categories.find((cat) => cat._id === selectedCategoryId) || null;
  }, [selectedCategoryId, categories]);

  const currentCategory = React.useMemo(() => {
    if (!currentParentId || !categories) return null;
    return categories.find((cat) => cat._id === currentParentId) || null;
  }, [currentParentId, categories]);

  const handleCategoryClick = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setSearchQuery("");
  };

  const handleBackToCategories = () => {
    setSelectedCategoryId(null);
    setSearchQuery("");
  };

  const handleBackToParentCategory = () => {
    setCurrentParentId(null);
  };

  const handleEditCategory = (category: RequestCategory) => {
    setEditingCategory(category);
    setMode("edit-category");
  };

  const handleBack = () => {
    setMode("list");
    setEditingCategory(null);
    setEditingRequest(null);
  };

  const activeCategoryId = selectedCategoryId || currentParentId;



  const openGallery = (id: string, options?: OpenGalleryOptions) => {
    if (options?.type) setType(options.type);
    if (options?.initialData) {
      setInitialData(options.initialData);
      setMode("create-request");
    } else {
      setInitialData(null);
      setMode("list");
    }
    if (options?.onSelect) setOnSelect(() => options.onSelect);
    else setOnSelect(undefined);

    setActiveGalleryId(id);
  };

  const closeGallery = () => {
    setActiveGalleryId(null);
    // Reset state after closing (optional, but good for cleanup)
    setTimeout(() => {
      setMode("list");
      setInitialData(null);
      setOnSelect(undefined);
      setSearchQuery("");
    }, 300);
  };

  const value = {
    searchQuery,
    setSearchQuery,
    selectedCategoryId,
    setSelectedCategoryId,
    currentParentId,
    setCurrentParentId,
    categories,
    isLoadingCategories,
    mode,
    setMode,
    editingCategory,
    editingRequest,
    setEditingRequest,
    handleEditCategory,
    filteredCategories,
    filteredRequests,
    selectedCategory,
    currentCategory,
    handleCategoryClick,
    handleBackToCategories,
    handleBackToParentCategory,
    handleBack,
    activeCategoryId,
    requests,
    isLoadingRequests,
    type,
    activeGalleryId,
    openGallery,
    closeGallery,
    initialData,
    onSelect,
    setType,
  };

  return <RequestGalleryContext.Provider value={value}>{children}</RequestGalleryContext.Provider>;
};

export const useRequestGallery = () => {
  const context = React.useContext(RequestGalleryContext);
  if (context === undefined) {
    throw new Error("useRequestGallery must be used within a RequestGalleryProvider");
  }
  return context;
};
